/**
 * Top-bar bell icon (#45 slice 4).
 *
 * Three visible elements:
 *   1. The bell glyph itself — a button that opens the dropdown.
 *   2. A small unread-count badge stacked on the corner — hidden when 0.
 *   3. A dropdown panel showing the most-recent notifications.
 *
 * Data:
 *   - Polls `api.notifications.unreadCount` every 30s. (We could use
 *     SSE/WebSockets later; polling keeps the SPA additive-only.)
 *   - Loads the list lazily on dropdown open so a tab full of unopened
 *     bells doesn't fetch hundreds of rows per minute.
 *
 * Click behaviour:
 *   - Clicking a notification follows its `linkUrl`. The PATCH that
 *     marks it read fires on click — the navigation happens immediately
 *     after; users don't wait on the round-trip.
 *   - "Mark all read" calls POST /api/v1/notifications/mark-all-read,
 *     invalidates queries.
 *
 * Out of scope for v1: bulk dismiss, "see all" history, kind-specific
 * icons beyond a single discriminator. Those land when there's a second
 * kind to differentiate.
 */
import { Bell, Check } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Button } from '@/components/ui/Button.js';
import { api, type Notification } from '@/api/client.js';
import { cn } from '@/lib/utils.js';

const UNREAD_POLL_MS = 30_000;

export function NotificationsBell(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  // Always-on poll for the badge — cheap (partial-index-backed
  // count(*)). 30s cadence is the floor before noise starts to matter;
  // tighten if operators tell us it feels stale.
  const unread = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.notifications.unreadCount(),
    refetchInterval: UNREAD_POLL_MS,
    // 401 here is benign on the login page; don't show toasts or retry
    // aggressively. The query naturally idles when the user logs out.
    retry: false,
    staleTime: 0,
  });
  const unreadCount = unread.data?.count ?? 0;

  // List only fetches when the dropdown is open. Keep the query alive
  // for a moment after close so reopening the same dropdown is
  // instant; long-stale revalidation keeps it cheap.
  const list = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => api.notifications.list({ limit: 20 }),
    enabled: open,
    staleTime: 10_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.notifications.update(id, { read: true }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => api.notifications.markAllRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  function handleClick(n: Notification): void {
    // Fire-and-forget the PATCH — navigating into the linked app is
    // the operator's reward for clicking, don't make them wait. The
    // unread count's next poll will reflect the change.
    if (!n.readAt) {
      markRead.mutate(n.id);
    }
    setOpen(false);
    if (n.linkUrl) {
      // Use the native location since linkUrl is a SPA-relative path
      // string — TanStack Router's typed navigate doesn't take raw
      // strings without losing type safety. Same effect, no router
      // schema dependency from the notification payload.
      void navigate({ to: n.linkUrl });
    }
  }

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
          title={unreadCount > 0 ? `${unreadCount} unread` : 'Notifications'}
          className="relative"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span
              aria-hidden
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-semibold leading-none text-primary-foreground"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="w-[360px] rounded-md border border-border bg-card p-1 text-sm shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Notifications
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
              >
                <Check className="h-3 w-3" />
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-auto">
            {list.isLoading && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">Loading…</div>
            )}
            {!list.isLoading && list.data && list.data.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                Nothing yet — push to a tracked repo and DeployIt will flag changes here.
              </div>
            )}
            {list.data?.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => handleClick(n)}
                className={cn(
                  'flex w-full flex-col items-start gap-0.5 border-b border-border/40 px-3 py-2 text-left last:border-b-0',
                  'hover:bg-accent/40',
                  !n.readAt && 'bg-primary/5',
                )}
              >
                <div className="flex w-full items-start justify-between gap-2">
                  <span
                    className={cn(
                      'truncate text-sm',
                      n.readAt ? 'text-muted-foreground' : 'font-medium text-foreground',
                    )}
                  >
                    {n.title}
                  </span>
                  {!n.readAt && (
                    <span
                      aria-hidden
                      className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                    />
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{n.body}</span>
                <span className="text-[10px] text-muted-foreground/70">
                  {formatRelative(n.createdAt)}
                </span>
              </button>
            ))}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * Hand-rolled relative time formatter — small, no extra dep. "5s ago",
 * "12m ago", "3h ago", "2d ago", else absolute ISO date. Falls back to
 * the raw string if Date.parse can't interpret it.
 */
function formatRelative(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const deltaSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86_400) return `${Math.floor(deltaSec / 3600)}h ago`;
  if (deltaSec < 86_400 * 7) return `${Math.floor(deltaSec / 86_400)}d ago`;
  return new Date(t).toISOString().slice(0, 10);
}
